import { useState, useEffect } from 'react';
import { API_URL } from '../../lib/constants';

interface OGData {
  title: string | null;
  description: string | null;
  image: string | null;
  site_name: string | null;
  url: string;
}

interface LinkPreviewProps {
  url: string;
  onLoad?: () => void;
}

export function LinkPreview({ url, onLoad }: LinkPreviewProps) {
  const [data, setData] = useState<OGData | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchOG() {
      try {
        const resp = await fetch(`${API_URL}/og?url=${encodeURIComponent(url)}`);
        if (!resp.ok) throw new Error('fetch failed');
        const result = await resp.json();
        if (cancelled) return;
        if (result.title || result.description || result.image) {
          setData(result);
          onLoad?.();
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchOG();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-neon hover:text-neon-dim transition-colors break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );
  }

  if (loading) {
    return (
      <div className="border border-border rounded-xl overflow-hidden bg-surface/50 animate-pulse mt-2">
        <div className="h-32 bg-zinc-800" />
        <div className="p-3 space-y-2">
          <div className="h-3 bg-zinc-800 rounded w-1/4" />
          <div className="h-4 bg-zinc-800 rounded w-3/4" />
          <div className="h-3 bg-zinc-800 rounded w-full" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const displayDomain = (() => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return data.site_name || url;
    }
  })();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-border rounded-xl overflow-hidden bg-surface/50 hover:border-border-bright transition-all mt-2"
      onClick={(e) => e.stopPropagation()}
    >
      {data.image && (
        <img
          src={data.image}
          alt={data.title || ''}
          className="w-full max-h-48 object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div className="p-3">
        <p className="text-xs text-muted truncate">{data.site_name || displayDomain}</p>
        {data.title && (
          <p className="font-semibold text-sm text-zinc-200 mt-0.5 line-clamp-2">{data.title}</p>
        )}
        {data.description && (
          <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{data.description}</p>
        )}
      </div>
    </a>
  );
}
